/** One climate risk — the page behind a row of the register (Batch 5).
 *
 * ## It borrows; it keeps no second copy
 *
 * There is no "get one risk" endpoint, and none was added. The page asks the register's own read
 * (`useRisks(true)` — closed risks included, because a closed risk is exactly the one an assurance
 * provider asks about) and finds its row by id. That is the polymorphic record page's rule — *"the
 * facts are borrowed, never re-rendered"* — and it is why the category, horizon and strategy words
 * come from `riskWords.ts` and the rescoring sentence from `RiskParts.tsx`: the register and this
 * page describe one row and must not describe it differently.
 *
 * ## A score is words, and so is everything near it
 *
 * `basis.Score` refuses to be multiplied (RSK-01), so the API sends a sentence and this page prints
 * it. There is no numeric score, no grid and no bar: a bar is a product of two ordinals drawn
 * instead of printed. The tiles above hold things to **do** — a review date, a count of open
 * actions, whether a rescoring is owed — and none of them is a rating.
 *
 * ## What the page cannot show, said in words
 *
 * - **Linked initiatives.** Nothing in the data links a risk to an initiative: the transition plan
 *   records initiatives and decisions about targets, not the risks they answer. The page says that
 *   instead of an empty panel that reads as "none".
 * - **History.** Risks are not versioned (the register says so too), so there is no "as it stood".
 *
 * ## No write controls
 *
 * Responding, adding an action and closing stay on the register, for the reason `SupplierAccount`
 * gives: a second path to the same write is how two screens come to disagree. The one link on from
 * here is the record page, where notes, flags and approvals live.
 */

import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import type { Risk } from "../api/queries";
import { useRisks } from "../api/queries";
import { Loaded } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { PersonRef } from "./PersonRef";
import { RecordLink } from "./RecordLink";
import { RescoringNote } from "./RiskParts";
import { CATEGORY, HORIZON, STRATEGY } from "./riskWords";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import { BasisAndLimits, Facts } from "./ScreenParts";
import type { FactSpec } from "./ScreenParts";
import { useScreen } from "./useScreen";
import parts from "./ScreenParts.module.css";
import styles from "./Screen.module.css";
import rec from "./Record.module.css";
import own from "./RiskDetail.module.css";

function cls(...names: Array<string | false | undefined>): string {
  return names.filter(Boolean).join(" ");
}

function Pair({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt className={rec.k}>{k}</dt>
      <dd className={rec.v}>{children}</dd>
    </>
  );
}

function BackToRegister() {
  return (
    <Link className={styles.fixLink} to="/risks" search={keepSearch} data-testid="risk-back">
      <ArrowLeft size={13} weight="bold" aria-hidden="true" /> Back to Climate risks
    </Link>
  );
}

function NotOnTheRegister({ riskId }: { riskId: string }) {
  return (
    <Panel title="Risk" column="1 / 13">
      {/* One sentence for an id that never existed and for one this session may not read: telling
          them apart would disclose that a risk exists. */}
      <p className={styles.note} data-testid="risk-not-found" data-risk-id={riskId}>
        There is no risk with that address on this workspace&rsquo;s register. It may have been
        mistyped, or belong to another workspace.
      </p>
      <BackToRegister />
    </Panel>
  );
}

function RiskBody({ risk }: { risk: Risk }) {
  const closed = risk.state === "closed";
  const open = risk.actions.filter((action) => !action.completed).length;
  const facts: FactSpec[] = [
    {
      key: "risk-state",
      label: "State",
      value: closed ? "Closed" : "Open",
      textual: true,
      note: closed ? "No longer being managed" : "Being managed, with an owner",
    },
    {
      key: "risk-next-review",
      label: "Next review",
      value: shortDate(risk.next_review_on),
      textual: true,
      note: risk.review_overdue ? "The scheduled review date has passed" : "Not yet due",
    },
    {
      key: "risk-open-actions",
      label: "Open response actions",
      value: String(open),
      note: `of ${risk.actions.length} recorded`,
    },
    {
      key: "risk-rescoring",
      label: "Rescoring",
      value: risk.needs_rescoring ? "Owed" : "Not owed",
      textual: true,
      note: risk.needs_rescoring ? "Scored on a scale that was replaced" : "Scored on the current scale",
    },
  ];

  return (
    <>
      <Facts facts={facts} label="This risk at a glance" />
      <div className={parts.area}>
        <div className={parts.twoCol}>
          <div className={cls(own.main, parts.stack)}>
            <Panel title="Assessment" sub={`${CATEGORY[risk.category] ?? risk.category} · ${HORIZON[risk.horizon] ?? risk.horizon}`} column="1 / 13">
              {/* The score, as the words the server sent — never a product, never a coloured cell. */}
              <p className={own.score} data-testid="risk-score">
                {risk.score}
              </p>
              <p className={own.description}>{risk.description}</p>
              {risk.needs_rescoring ? <RescoringNote /> : null}
              <dl className={cls(rec.kv, own.kv)} data-testid="risk-facts">
                <Pair k="Response">{STRATEGY[risk.strategy] ?? risk.strategy}</Pair>
                <Pair k="Complete when">{risk.completion_criteria}</Pair>
                <Pair k="Review if">{risk.review_trigger}</Pair>
                <Pair k="Next review">
                  {shortDate(risk.next_review_on)}{" "}
                  {risk.review_overdue ? <StatusPill tone="warn">Overdue</StatusPill> : null}
                </Pair>
                <Pair k="Owner">
                  <PersonRef userId={risk.owner_user_id} showName />
                </Pair>
              </dl>
            </Panel>

            <Panel
              title="Response actions"
              sub={risk.actions.length ? `${open} open of ${risk.actions.length}` : "none recorded"}
              column="1 / 13"
            >
              {risk.actions.length === 0 ? (
                <Empty>
                  No response action has been recorded for this risk. A response is what the company
                  will do about it; add one from the register.
                </Empty>
              ) : (
                <ul className={own.actions} data-testid="risk-actions">
                  {risk.actions.map((action) => (
                    <li key={action.id} className={own.action} data-completed={action.completed}>
                      <span className={own.actionHead}>
                        <StatusPill tone={action.completed ? "ok" : "info"}>
                          {action.completed ? "Done" : "Open"}
                        </StatusPill>
                        <span className={own.actionWhat}>{action.description}</span>
                      </span>
                      <span className={own.actionMeta}>
                        due {shortDate(action.due_on)} · owner{" "}
                        <PersonRef userId={action.owner_user_id} showName />
                        {action.evidence_document_id ? (
                          <>
                            {" · "}
                            <Link
                              className={styles.fixLink}
                              to="/evidence/$documentId"
                              params={{ documentId: action.evidence_document_id }}
                              search={keepSearch}
                              data-testid="risk-action-evidence"
                            >
                              Evidence document ›
                            </Link>
                          </>
                        ) : null}
                        {/* **Evidence is a document.** A completed action with none is a response
                            nobody can show an assurance provider, said beside it in words. */}
                        {action.completed && !action.evidence_document_id ? (
                          <>
                            {" · "}
                            <StatusPill tone="warn">No document</StatusPill>
                          </>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          <div className={cls(own.side, parts.stack)}>
            <Panel title="Linked work" column="1 / 13">
              <p className={styles.note}>
                Notes, flags and approvals about this risk live on its record, not here.
              </p>
              <p>
                <RecordLink recordId={risk.id} kind="climate_risk" subject={risk.title} />
              </p>
              {/* **Said, not left blank.** Nothing in the data links an initiative to a risk, so an
                  empty list here would read as "no initiative answers this risk", a claim the
                  product has no way to make. */}
              <p className={styles.note} data-testid="risk-no-initiative-link">
                Which initiatives in the transition plan answer this risk is not recorded: the plan
                holds initiatives and targets, and neither names a risk.
              </p>
              <p className={styles.note}>
                Adding an action, completing one and closing the risk are done on the register.
              </p>
            </Panel>
          </div>
        </div>
      </div>
      <div className={parts.area}>
        <BasisAndLimits testId="risk-basis">
          <p>
            A score is the position the company chose on its own scale, printed as words. It is not
            multiplied, summed or ranked, because the scale does not support the difference between
            a 15 and a 20.
          </p>
          <p>
            This is the risk as it stands today. Risks are not versioned, so it cannot be shown as it
            stood when a report was issued; what was <em>stated</em> is pinned by the filing itself.
          </p>
        </BasisAndLimits>
      </div>
    </>
  );
}

function RiskScreenBody() {
  const screen = useScreen();
  const { riskId } = useParams({ from: "/risks/$riskId" });
  //: The query, not `data = []`: a dropped request is not a risk that does not exist (EXP-29).
  const register = useRisks(true);
  const risk = register.data?.find((entry) => entry.id === riskId);

  return (
    <ScreenFrame
      screen={screen}
      title={risk?.title ?? "Climate risk"}
      crumb="one climate risk, as the register holds it"
      bodyScrolls
    >
      <PageHeader
        subtitle="What this risk is, who owns it, and what the company intends to do. The score is words, never a number."
        actions={<BackToRegister />}
      />
      <Loaded
        query={register}
        of="The risk register"
        empty={<NotOnTheRegister riskId={riskId} />}
      >
        {(rows) => {
          const found = rows.find((entry) => entry.id === riskId);
          return found ? <RiskBody risk={found} /> : <NotOnTheRegister riskId={riskId} />;
        }}
      </Loaded>
    </ScreenFrame>
  );
}

export function RiskScreen() {
  return (
    <RequireSession>
      <RiskScreenBody />
    </RequireSession>
  );
}
